import React, { useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import { getErrorMessage } from "services/api";
import { useStores } from "shared-apis";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace, Divider } from "components/Spacing";
import {
  useModal,
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ConfirmationModal,
  InfoModal,
} from "components/Modal";

import { createStation, updateStation, unbindStationDevice } from "./api";
import { StationSettingsEditor } from "./StationSettingsEditor";
import styles from "./styles.module.scss";

const LABEL = 150;
const INPUT_W = 260;

/** Дефолтный приоритет новой станции: max(занятых) + 1 (меньше = выше приоритет) */
export const getDefaultPriority = (stations) => {
  const priorities = (stations ?? [])
    .map((s) => Number(s.priority))
    .filter(Number.isFinite);
  if (!priorities.length) return 1;
  return Math.min(99, Math.max(...priorities) + 1);
};

/** Модалка создания/редактирования станции */
export const StationModal = ({ station, stations }) => {
  const queryClient = useQueryClient();
  const { openModal2, closeModal, closeModal2 } = useModal();

  const isEdit = Boolean(station?.stationid);

  const [name, setName] = useState(station?.name ?? "");
  const [importcode, setImportcode] = useState(station?.importcode ?? "");
  const [storesid, setStoresid] = useState(
    station?.storesid != null ? String(station.storesid) : ""
  );
  const [priority, setPriority] = useState(() =>
    station ? String(station.priority) : String(getDefaultPriority(stations))
  );
  const [settings, setSettings] = useState(() => {
    if (!station?.settings) return "";
    try {
      return JSON.stringify(JSON.parse(station.settings), null, 2);
    } catch {
      return station.settings;
    }
  });
  const [deviceuniqueid, setDeviceuniqueid] = useState(
    station?.deviceuniqueid ?? ""
  );

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [unbinding, setUnbinding] = useState(false);

  const storesQuery = useStores();
  const storeOptions = useMemo(
    () =>
      (storesQuery.data?.items ?? []).map((s) => ({
        value: String(s.storesid),
        label: s.name,
      })),
    [storesQuery.data]
  );
  const storeVal =
    storeOptions.find((o) => String(o.value) === String(storesid)) || null;

  /* ---- отвязка устройства ---- */

  const handleUnbind = () => {
    openModal2(
      <ConfirmationModal
        title="Отвязать устройство от станции? Станция снова станет свободной."
        confirmText="Отвязать"
        danger={false}
        onConfirm={async () => {
          setUnbinding(true);
          try {
            await unbindStationDevice(station.stationid);
            setDeviceuniqueid("");
            queryClient.invalidateQueries(["stations"]);
            closeModal2();
          } catch (e) {
            openModal2(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal2} />
            );
          } finally {
            setUnbinding(false);
          }
        }}
        onClose={closeModal2}
      />
    );
  };

  /* ---- сохранение ---- */

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Укажите наименование");
      return;
    }
    if (!storesid) {
      setError("Выберите склад продаж");
      return;
    }
    const priorityNum = Number(priority);
    if (!Number.isInteger(priorityNum) || priorityNum < 1 || priorityNum > 99) {
      setError("Приоритет должен быть целым числом от 1 до 99");
      return;
    }

    let settingsPayload = null;
    if (settings.trim()) {
      try {
        const parsed = JSON.parse(settings);
        if (
          parsed === null ||
          typeof parsed !== "object" ||
          Array.isArray(parsed)
        ) {
          setError("Настройки должны быть JSON-объектом, например {\"key\": true}");
          return;
        }
        settingsPayload = JSON.stringify(parsed);
      } catch {
        setError("Настройки содержат невалидный JSON");
        return;
      }
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        importcode: importcode.trim() || null,
        storesid: Number(storesid),
        priority: priorityNum,
        settings: settingsPayload,
      };
      if (isEdit) {
        await updateStation({ ...payload, stationid: station.stationid });
      } else {
        await createStation(payload);
      }
      queryClient.invalidateQueries(["stations"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell className={styles.modal}>
      <ModalHeader
        title={isEdit ? `Станция "${station.name}"` : "Новая станция"}
        onClose={closeModal}
      />
      <ModalBody>
        <div className={styles.form}>
          {isEdit && (
            <>
              <Input
                label="Код"
                labelWidth={LABEL}
                value={station.stationid}
                disabled
                inputWidth={100}
              />
              <VerticalSpace size={12} />
            </>
          )}
          <Input
            label="Наименование"
            labelWidth={LABEL}
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            inputWidth={INPUT_W}
          />
          <VerticalSpace size={12} />
          <Input
            label="Внешний код"
            labelWidth={LABEL}
            value={importcode}
            onChange={(ev) => setImportcode(ev.target.value)}
            inputWidth={INPUT_W}
          />
          <VerticalSpace size={12} />
          <Select
            label="Склад продаж"
            labelWidth={LABEL}
            options={storeOptions}
            value={storeVal}
            onChange={(val) => setStoresid(val?.value ?? "")}
            isLoading={storesQuery.isLoading}
            placeholder="Выберите склад"
            width={INPUT_W}
          />
          <VerticalSpace size={12} />
          <Input
            label="Приоритет"
            labelWidth={LABEL}
            type="number"
            value={priority}
            onChange={(ev) => setPriority(ev.target.value)}
            inputWidth={100}
          />
          <VerticalSpace size={4} />
          <RegularText size={12} color="muted" className={styles.hint}>
            1 — самый высокий приоритет, 99 — самый низкий
          </RegularText>

          {isEdit && (
            <>
              <VerticalSpace size={16} />
              <Divider />
              <VerticalSpace size={16} />
              <div className={styles.deviceRow}>
                <MediumText size={13} style={{ width: LABEL, flexShrink: 0 }}>
                  Устройство:
                </MediumText>
                {deviceuniqueid ? (
                  <>
                    <RegularText size={13} className={styles.deviceId}>
                      {deviceuniqueid}
                    </RegularText>
                    <Button
                      variant="tertiary"
                      onClick={handleUnbind}
                      loading={unbinding}
                    >
                      Отвязать устройство
                    </Button>
                  </>
                ) : (
                  <RegularText size={13} color="muted">
                    Станция свободна
                  </RegularText>
                )}
              </div>
            </>
          )}

          <VerticalSpace size={16} />
          <Divider />
          <VerticalSpace size={16} />
          <StationSettingsEditor
            key={station?.stationid ?? "new"}
            value={settings}
            onChange={setSettings}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        {!!error && (
          <RegularText size={13} color="danger" className={styles.footerError}>
            {error}
          </RegularText>
        )}
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
        <Button variant="tertiary" onClick={closeModal}>
          Отмена
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
